import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Glyph, hasGlyph, type GlyphMeaning } from "@apf/design-system/glyphs";
import { PlaceTrail } from "../components/PlaceTrail";
import { WorkMomentIllustration } from "../components/WorkMomentIllustration";
import {
  activeWorkblockDimensionFacetDefinitions,
  businessModulePath,
  businessModulesFor,
  canonicalizeFacetIds,
  domainWorkblockPath,
  downloadAssetPath,
  groundworksEntityPath,
  integrationBindingForModule,
  integrationExamplePath,
  integrationRecipePath,
  integrationRecipesForModule,
  integrationToolForModule,
  resolveWorkMoment,
  roleDefinitions,
  rolePath,
  workblockDimensionFacetDefinitions,
  workblockDimensionMemberIds,
  workblockMatchesFacets,
  workMomentsForDomain,
  workMomentsForJob,
  workMomentPath,
  type BusinessModule,
  type DomainWorkblock,
  type WorkblockDimensionFacetParam
} from "../catalogue";
import { useCopy, useFramework } from "../framework";
import { iconFor } from "../components/icons";
import {
  ArrowLinkLabel,
  AbsenceBlock,
  BeforeAfter,
  EmptyState,
  FactRow,
  PageHeader,
  SectionHeading,
  StatementSection,
  StatusLegend,
  StatusMark,
  humanize,
  ordinal
} from "../components/ui";
import { CatalogueFacet, ContextRecordPreview } from "../components/catalogue";
import { BusinessModuleIndexRow, useFrameworkModel } from "./FrameworkPages";
import { usePageTitle, useScrollToHash } from "./support";
import { partnerCapabilityLinksForTarget } from "../capabilityMapping";
import { CapabilityMapping } from "../components/CapabilityMapping";
import { CopyAssetButton, copyableFormats, useAssetManifest, type ManifestAsset } from "../downloadsManifest";
import { assetHref } from "../routerMount";
import {
  WorkblockList as list,
  WorkblockSectionTitle as SectionTitle
} from "./WorkblockPagePrimitives";

const ArrowRight = iconFor("arrow-right");
const Search = iconFor("search");
type WorkblockFacetKey = "role" | "job" | WorkblockDimensionFacetParam;
const workblockFacetKeys: readonly WorkblockFacetKey[] = ["role", "job", "segment", "buying", "treatment"];

function replaceTokens(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce((result, [key, value]) => result.replaceAll(`{${key}}`, String(value)), template);
}

function replaceCount(template: string, count: number): string {
  return template.replace("{count}", String(count));
}

function replaceLabel(template: string, label: string): string {
  return template.replace("{label}", label);
}

function replaceValues(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    template
  );
}

/**
 * Keep the Workblock discovery URL stable and audience-safe. Unknown role and
 * job IDs remain in the URL long enough to be reported as unavailable, but
 * duplicates, empty values and parameters outside the 5.1 contract do not.
 */
export function canonicalWorkblockSearchParams(
  current: URLSearchParams,
  roleCatalogueIds: readonly string[],
  jobCatalogueIds: readonly string[],
  dimensionCatalogueIds: Partial<Record<WorkblockDimensionFacetParam, readonly string[]>> = {}
): URLSearchParams {
  const canonical = new URLSearchParams();
  const query = current.get("q");
  if (query) canonical.set("q", query);
  for (const id of canonicalizeFacetIds(current.getAll("role").filter(Boolean), roleCatalogueIds)) {
    canonical.append("role", id);
  }
  for (const id of canonicalizeFacetIds(current.getAll("job").filter(Boolean), jobCatalogueIds)) {
    canonical.append("job", id);
  }
  for (const { param } of workblockDimensionFacetDefinitions) {
    for (const id of canonicalizeFacetIds(current.getAll(param).filter(Boolean), dimensionCatalogueIds[param] ?? [])) {
      canonical.append(param, id);
    }
  }
  return canonical;
}

function searchString(params: URLSearchParams): string {
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

const workblockDimensionFacetCopy = {
  segment: {
    labelKey: "catalogue.workblocks.segment",
    labelFallback: "Spend segment",
    matchKey: "catalogue.workblocks.match.segment",
    matchFallback: "Spend segment",
    members: {
      direct: ["catalogue.workblocks.segment.direct", "Direct"],
      indirect: ["catalogue.workblocks.segment.indirect", "Indirect"]
    }
  },
  buying: {
    labelKey: "catalogue.workblocks.buying",
    labelFallback: "Buying mode",
    matchKey: "catalogue.workblocks.match.buying",
    matchFallback: "Buying mode",
    members: {
      goods: ["catalogue.workblocks.buying.goods", "Goods"],
      services: ["catalogue.workblocks.buying.services", "Services"],
      mixed: ["catalogue.workblocks.buying.mixed", "Mixed"]
    }
  },
  treatment: {
    labelKey: "catalogue.workblocks.treatment",
    labelFallback: "Accounting treatment",
    matchKey: "catalogue.workblocks.match.treatment",
    matchFallback: "Accounting treatment",
    members: {
      capital: ["catalogue.workblocks.treatment.capital", "Capital"],
      operating: ["catalogue.workblocks.treatment.operating", "Operating"]
    }
  }
} as const;

function dimensionFacetLabel(copy: ReturnType<typeof useCopy>, facet: WorkblockDimensionFacetParam): string {
  const config = workblockDimensionFacetCopy[facet];
  return copy(config.labelKey, config.labelFallback);
}

function dimensionMatchLabel(copy: ReturnType<typeof useCopy>, facet: WorkblockDimensionFacetParam): string {
  const config = workblockDimensionFacetCopy[facet];
  return copy(config.matchKey, config.matchFallback);
}

function dimensionMemberLabel(
  copy: ReturnType<typeof useCopy>,
  facet: WorkblockDimensionFacetParam,
  memberId: string
): string {
  const member = workblockDimensionFacetCopy[facet].members[
    memberId as keyof typeof workblockDimensionFacetCopy[typeof facet]["members"]
  ];
  return member ? copy(member[0], member[1]) : humanize(memberId);
}

function relationshipMatchIds(
  domainWorkblock: DomainWorkblock,
  matchedRoleIds: readonly string[],
  matchedJobIds: readonly string[]
): { roleIds: string[]; jobIds: string[] } {
  if (!matchedRoleIds.length || !matchedJobIds.length) {
    return { roleIds: [...matchedRoleIds], jobIds: [...matchedJobIds] };
  }
  const jobIds = new Set(matchedJobIds);
  const roles = domainWorkblock.roleCards.filter(({ id }) => matchedRoleIds.includes(id));
  const connectedRoles = roles.filter((role) =>
    [...role.performsBusinessModuleIds, ...role.consultedBusinessModuleIds].some((id) => jobIds.has(id))
  );
  const connectedRoleIds = new Set(connectedRoles.map(({ id }) => id));
  return {
    roleIds: matchedRoleIds.filter((id) => connectedRoleIds.has(id)),
    jobIds: matchedJobIds.filter((id) => connectedRoles.some((role) =>
      role.performsBusinessModuleIds.includes(id) || role.consultedBusinessModuleIds.includes(id)
    ))
  };
}

export interface JobsCatalogueReturnState {
  catalogue: "jobs";
  returnTo: string;
}

export interface WorkblocksCatalogueReturnState {
  catalogue: "workblocks";
  returnTo: string;
}

function jobsCatalogueReturnState(value: unknown): JobsCatalogueReturnState | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Partial<JobsCatalogueReturnState>;
  if (candidate.catalogue !== "jobs" || typeof candidate.returnTo !== "string") return undefined;
  if (!candidate.returnTo.startsWith("/reference/jobs") || candidate.returnTo.startsWith("//")) return undefined;
  try {
    const url = new URL(candidate.returnTo, "https://groundworks.local");
    return url.origin === "https://groundworks.local" && url.pathname === "/reference/jobs"
      ? { catalogue: "jobs", returnTo: `${url.pathname}${url.search}${url.hash}` }
      : undefined;
  } catch {
    return undefined;
  }
}

function workblocksCatalogueReturnState(value: unknown): WorkblocksCatalogueReturnState | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Partial<WorkblocksCatalogueReturnState>;
  if (candidate.catalogue !== "workblocks" || typeof candidate.returnTo !== "string") return undefined;
  if (!candidate.returnTo.startsWith("/workblocks") || candidate.returnTo.startsWith("//")) return undefined;
  try {
    const url = new URL(candidate.returnTo, "https://groundworks.local");
    return url.origin === "https://groundworks.local" && url.pathname === "/workblocks"
      ? { catalogue: "workblocks", returnTo: `${url.pathname}${url.search}${url.hash}` }
      : undefined;
  } catch {
    return undefined;
  }
}

function PublishedBusinessModuleDetail({
  domainWorkblock,
  businessModule
}: {
  domainWorkblock: DomainWorkblock;
  businessModule: BusinessModule;
}) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const location = useLocation();
  const jobsReturnState = jobsCatalogueReturnState(location.state);
  const workblocksReturnState = workblocksCatalogueReturnState(location.state);
  const connectedRoles = domainWorkblock.roleCards.filter(
    (role) =>
      role.performsBusinessModuleIds.includes(businessModule.id) ||
      role.consultedBusinessModuleIds.includes(businessModule.id)
  );
  const records = snapshot.groundworks.entities.filter(
    (entity) =>
      entity.ownerBusinessModuleId === businessModule.id ||
      entity.consumerBusinessModuleIds.includes(businessModule.id)
  );
  const consumedRecords = records.filter((entity) => entity.ownerBusinessModuleId !== businessModule.id);
  const ownedRecords = records.filter((entity) => entity.ownerBusinessModuleId === businessModule.id);
  const controlItems = [...new Set([
    ...businessModule.agents.flatMap((agent) => agent.escalationTriggers),
    ...businessModule.autonomy.alwaysEscalate
  ])];
  const siblingModules = businessModulesFor(snapshot, domainWorkblock.id).filter((sibling) => sibling.id !== businessModule.id);
  const personaById = new Map(snapshot.businessModuleRegistry.personas.map((persona) => [persona.id, persona]));
  const capabilityMappings = partnerCapabilityLinksForTarget(snapshot, copy, "business-module", businessModule.id);
  const integrationBinding = integrationBindingForModule(snapshot.integrations, businessModule.id);
  const integrationRecipes = integrationRecipesForModule(snapshot.integrations, businessModule.id);
  const toolRecipe = integrationToolForModule(snapshot.integrations, businessModule.id);
  const systemById = new Map(snapshot.integrations.systemProfiles.map((system) => [system.id, system]));
  const inboundIds = new Set(integrationBinding?.inboundRecipeIds ?? []);
  const outboundIds = new Set(integrationBinding?.outboundRecipeIds ?? []);
  const toolAgents = toolRecipe?.agentIds.flatMap((id) => {
    const agent = businessModule.agents.find((candidate) => candidate.id === id);
    return agent ? [agent] : [];
  }) ?? [];
  const jobStoryById = new Map(businessModule.jobStories.map((story) => [story.id, story]));
  const workMoments = workMomentsForJob(snapshot, businessModule.id);
  const principalMoment = workMoments[0];
  const roleLabelById = new Map(roleDefinitions(snapshot).map(({ definition }) => [definition.id, copy(definition.labelKey)]));
  const alternatePerspectives = principalMoment?.perspectives
    .filter(({ roleId }) => roleId !== principalMoment.defaultPerspectiveRoleId)
    .slice(0, 3) ?? [];
  usePageTitle(copy(businessModule.labelKey));
  /* Search deep-links land on #flow-<id> and #agent-<id> anchors inside closed disclosures. */
  useScrollToHash();

  return (
    <div className="apf-page apf-register-reference apf-workblocks-page">
      {jobsReturnState
        ? <Link className="apf-button apf-button--quiet apf-back-link" to={jobsReturnState.returnTo}>{copy("catalogue.workblocks.job-detail.back-to-jobs", "Back to jobs to be done")}</Link>
        : workblocksReturnState
          ? <Link className="apf-button apf-button--quiet apf-back-link" to={workblocksReturnState.returnTo}>{copy("page.workblocks.module.back-to-workblocks", "Back to all Workblocks")}</Link>
          : null}
      <PlaceTrail stops={[
        { label: copy("component.place-trail.framework", "Framework"), to: "/" },
        { label: copy(domainWorkblock.nameKey), to: domainWorkblockPath(domainWorkblock.id) },
        { label: copy(businessModule.labelKey) }
      ]} />

      <div className="apf-detail-hero">
        <section className="apf-detail-hero__main apf-surface">
          <p className="apf-page-header__eyebrow">{copy("page.workblocks.module.eyebrow", "Business module")} · {copy(domainWorkblock.nameKey)}</p>
          <h1>{copy(businessModule.labelKey)}</h1>
          {copy(businessModule.descriptorKey) !== copy(businessModule.labelKey) ? <p className="apf-detail-hero__descriptor">{copy(businessModule.descriptorKey)}</p> : null}
          <p className="apf-detail-hero__status"><StatusMark value={businessModule.implementationStatus} /></p>
          <p className="apf-detail-hero__job"><Glyph meaning="class.job" size={20} className="apf-lead-glyph" />{copy(businessModule.jobStatementKey)}</p>
          <p>{copy(businessModule.descriptionKey)}</p>
          <div className="apf-detail-actions">
            <Link className="apf-button apf-button--secondary" to={domainWorkblockPath(domainWorkblock.id)}>{copy("page.workblocks.module.open-parent", "Open parent Workblock")}</Link>
          </div>
        </section>
        <aside className="apf-detail-hero__aside apf-surface">
          <dl>
            <dt>{copy("page.workblocks.module.fact.outcome", "Outcome")}</dt><dd>{copy(businessModule.outcomeKey)}</dd>
            <dt>{copy("page.workblocks.module.fact.sibling-modules", "Business modules in the parent")}</dt><dd>{domainWorkblock.businessModuleIds.length}</dd>
            <dt>{copy("page.workblocks.module.fact.records", "Connected records")}</dt><dd>{records.length}</dd>
            <dt>{copy("page.workblocks.module.fact.flows", "Execution flows")}</dt><dd>{businessModule.executionFlows.length}</dd>
            {businessModule.agents.length ? <><dt>{copy("page.workblocks.module.fact.agents", "Governed agents")}</dt><dd>{businessModule.agents.length}</dd></> : null}
          </dl>
        </aside>
      </div>

      <CapabilityMapping
        label={copy("partner-capability.detail.heading")}
        items={capabilityMappings}
      />

      <nav className="apf-detail-index apf-surface" aria-label={copy("page.workblocks.module.nav.aria-label", "Business module sections")}>
        {[
          ["problem", copy("page.workblocks.module.nav.problem", "Problem")],
          ["value", copy("page.workblocks.module.nav.value", "Outcome")],
          ["job-stories", copy("page.workblocks.module.nav.job-stories", "Job stories")],
          ...(businessModule.currentStateKey && businessModule.futureStateKey ? [["process", copy("page.workblocks.module.nav.process", "Process")]] : []),
          ["authority", copy("page.workblocks.module.nav.authority", "Authority")],
          ...(businessModule.agents.length ? [["agents", copy("page.workblocks.module.nav.agents", "Agents")]] : []),
          ...(integrationBinding ? [["connections", copy("page.connected-work.module.nav.connections")]] : []),
          ["required", copy("page.workblocks.module.nav.required", "Required")],
          ...(businessModule.metrics.length ? [["measured", copy("page.workblocks.module.nav.measured", "Measured")]] : []),
          ["controls", copy("page.workblocks.module.nav.controls", "Controls")],
          ["execution-flows", copy("page.workblocks.module.nav.execution-flows", "Execution flows")],
          ["resources", copy("page.workblocks.module.nav.resources", "Working material")],
          ["related", copy("page.workblocks.module.nav.related", "Related")]
        ].map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
      </nav>

      <section className="apf-page-section" id="problem">
        <SectionTitle eyebrow={copy("page.workblocks.module.problem.eyebrow", "The business problem")} title={copy("page.workblocks.module.problem.title", "Where value leaks today")} />
        <div className="apf-explainer-prose">
          <p>{copy(businessModule.hiredInsteadKey)}</p>
          {businessModule.currentStateKey ? <p>{copy(businessModule.currentStateKey)}</p> : null}
        </div>
      </section>

      <section className="apf-page-section" id="value">
        <SectionTitle eyebrow={copy("page.workblocks.module.value.eyebrow", "Bounded outcome")} title={copy("page.workblocks.module.value.title", "The outcome this function owns")} />
        <article className="apf-pattern-system apf-surface"><h3>{copy("page.workblocks.module.value.core-value", "Core value")}</h3><p>{businessModule.coreValue}</p></article>
        {businessModule.agentStoryKey ? <div className="apf-two-column"><article className="apf-surface apf-pattern-panel"><h3>{copy("page.workblocks.module.value.agent-story", "Safe-autonomy exchange")}</h3><p>{copy(businessModule.agentStoryKey)}</p></article></div> : null}
      </section>

      <section className="apf-page-section" id="job-stories">
        <SectionTitle eyebrow={copy("page.workblocks.module.job-stories.eyebrow", "Approved subordinate jobs")} title={copy("page.workblocks.module.job-stories.title", "The situations it is hired for")} description={copy("page.workblocks.module.job-stories.description", "Each approved subordinate job sits beneath the module's one governed job and names the durable evidence that closes the work.")} />
        <p className="apf-detail-hero__job apf-story-parent"><Glyph meaning="class.job" size={20} className="apf-lead-glyph" />{copy(businessModule.jobStatementKey)}</p>
        <ul className="apf-story-list">
          {businessModule.jobStories.map((story) => (
            <li className="apf-story" key={story.id}>
              <p className="apf-story__text">{copy(story.storyKey)}</p>
              <p className="apf-story__done"><span className="apf-story__done-label">{copy("page.workblocks.module.job-stories.done-when", "Done when")}</span><span>{copy(story.completionKey)}</span></p>
            </li>
          ))}
        </ul>
      </section>

      {businessModule.currentStateKey && businessModule.futureStateKey ? (
        <section className="apf-page-section" id="process">
          <SectionTitle eyebrow={copy("page.workblocks.module.process.eyebrow", "Current versus future state")} title={copy("page.workblocks.module.process.title", "From today's process to the governed one")} />
          <div className="apf-change__pair">
            <div className="apf-change__state">
              <p className="apf-change__kicker">{copy("page.workblocks.module.process.today", "Today")}</p>
              <p>{copy(businessModule.currentStateKey)}</p>
            </div>
            <div className="apf-change__state">
              <p className="apf-change__kicker">{copy("page.workblocks.module.process.governed", "Governed")}</p>
              <p>{copy(businessModule.futureStateKey)}</p>
            </div>
          </div>
        </section>
      ) : null}

      {principalMoment ? <section className="apf-page-section" id="work-moment" aria-labelledby="work-moment-title"><SectionTitle id="work-moment-title" eyebrow={copy("work-moment.proof.illustrative", "Illustrative work moment")} title={copy("work-moment.job-preview.title", "See the job in practice")} description={copy("work-moment.job-preview.description", "One bounded example connects this job to its module agent, human authority, evidence and learning.")} /><ol className="apf-catalogue-index"><li className="apf-catalogue-index__row"><Link className="apf-catalogue-index__link" to={workMomentPath(principalMoment.id, principalMoment.defaultPerspectiveRoleId)}><strong>{copy(principalMoment.titleKey)}</strong><span>{copy(principalMoment.summaryKey)}</span><span className="apf-catalogue-index__meta">{copy("work-moment.proof.illustrative", "Illustrative work moment")} · {copy("work-moment.proof.synthetic", "Synthetic data")}</span></Link></li></ol>{alternatePerspectives.length ? <p>{copy("work-moment.job-preview.other-perspectives", "Other role perspectives")}: {alternatePerspectives.map((perspective, index) => <span key={perspective.roleId}>{index ? " · " : null}<Link to={workMomentPath(principalMoment.id, perspective.roleId)}>{roleLabelById.get(perspective.roleId) ?? perspective.roleId}</Link></span>)}</p> : null}<p><Link to={`/reference/work-moments?job=${encodeURIComponent(businessModule.id)}`}>{copy("work-moment.job-preview.browse-all", "Browse all work moments for this job")}</Link></p></section> : null}

      <section className="apf-page-section" id="authority">
        <SectionTitle eyebrow={copy("page.workblocks.module.authority.eyebrow", "Human authority")} title={copy("page.workblocks.module.authority.title", "A named person approves what commits")} description={copy("page.workblocks.module.authority.description", "Proposal and commitment remain separate for this bounded outcome.")} />
        <div className="apf-feature-group-grid">
          {connectedRoles.map((role) => (
            <article className="apf-surface apf-pattern-panel" key={role.id}>
              <p className="apf-section-heading__eyebrow">
                {role.performsBusinessModuleIds.includes(businessModule.id) ? copy("page.workblocks.module.authority.performs", "Performs this module") : copy("page.workblocks.module.authority.consulted", "Consulted on this module")}
              </p>
              <h3>{copy(role.roleKey)}</h3>
              <p>{copy(role.mandateKey)}</p>
              <dl className="apf-definition-list">
                <div><dt>{copy("catalogue.workblocks.job-detail.connection", "Connection")}</dt><dd>{role.performsBusinessModuleIds.includes(businessModule.id) ? copy("catalogue.workblocks.job-detail.performer", "Performer") : copy("catalogue.workblocks.job-detail.consulted", "Consulted")}</dd></div>
                <div><dt>{copy("page.workblocks.module.authority.may-propose", "May propose")}</dt><dd>{copy(role.proposeKey)}</dd></div>
                <div><dt>{copy("page.workblocks.module.authority.may-commit", "May commit")}</dt><dd>{copy(role.commitKey)}</dd></div>
                <div><dt>{copy("page.workblocks.module.authority.never-alone", "Never alone")}</dt><dd>{copy(role.aloneKey)}</dd></div>
              </dl>
            </article>
          ))}
        </div>
      </section>

      {businessModule.agents.length ? (
        <section className="apf-page-section" id="agents">
          <SectionTitle
            eyebrow={copy("page.workblocks.module.agents.eyebrow", "Governed agents")}
            title={copy("page.workblocks.module.agents.title", "The agent executes inside a revocable envelope")}
            description={copy("page.workblocks.module.agents.description", "An agent proposes and acts only inside a revocable envelope; a named person commits. The charter is public: mission, envelope, escalation and the people it serves.")}
          />
          {businessModule.agents.map((agent) => {
            const promptVersion = "promptVersion" in agent ? agent.promptVersion : agent.contractVersions.prompt;
            const personaLabels = agent.personaIds.map((id) => personaById.get(id)?.label ?? humanize(id));
            const servedLabels = agent.servedRoleIds.map((id) => roleLabelById.get(id) ?? humanize(id));
            const operatorLabels = agent.operatorRoleIds.map((id) => roleLabelById.get(id) ?? humanize(id));
            return (
              <article className="apf-agent-charter" id={`agent-${agent.id}`} key={agent.id}>
                <header className="apf-agent-charter__header">
                  {hasGlyph("capability.agent-tools") ? <Glyph meaning="capability.agent-tools" size={24} /> : null}
                  <div>
                    <h3>{agent.label}</h3>
                    <p className="apf-agent-charter__meta"><code>{agent.id}</code>{" · "}{replaceTokens(copy("page.workblocks.module.agents.charter-version", "Charter v{version}"), { version: promptVersion })}</p>
                  </div>
                </header>
                <p className="apf-agent-charter__mission">{agent.mission}</p>
                <dl className="apf-definition-list apf-agent-charter__rows">
                  <div><dt>{copy("page.workblocks.module.agents.works-for", "Works for")}</dt><dd>{personaLabels.join(", ")}</dd></div>
                  <div><dt>{copy("page.workblocks.module.agents.answers-to", "Answers to")}</dt><dd>{servedLabels.join(", ")}</dd></div>
                  <div><dt>{copy("page.workblocks.module.agents.operated-by", "Operated by")}</dt><dd>{operatorLabels.join(", ")}</dd></div>
                  <div><dt>{copy("page.workblocks.module.agents.acts-alone", "Acts alone when")}</dt><dd>{list(agent.autonomousActions)}</dd></div>
                  <div><dt>{copy("page.workblocks.module.agents.hands-back", "Hands back when")}</dt><dd>{list(agent.escalationTriggers)}</dd></div>
                </dl>
              </article>
            );
          })}
        </section>
      ) : null}

      {integrationBinding ? (
        <section className="apf-page-section" id="connections">
          <SectionTitle eyebrow={copy("page.connected-work.module.eyebrow")} title={copy("page.connected-work.module.title")} description={copy("page.connected-work.module.description")} />
          <div className="apf-module-connection-summary">
            <article>
              <p className="apf-section-heading__eyebrow">{copy("page.connected-work.module.canonical-path-label")}</p>
              <h3>{replaceTokens(copy(integrationRecipes.length === 1 ? "page.connected-work.module.recipe-count.singular" : "page.connected-work.module.recipe-count"), { recipes: integrationRecipes.length })}</h3>
              <ul>{integrationRecipes.map((recipe) => {
                const directionKey = inboundIds.has(recipe.id) && outboundIds.has(recipe.id)
                  ? "bidirectional"
                  : inboundIds.has(recipe.id) ? "inbound" : "outbound";
                return <li key={recipe.id}><Link to={integrationRecipePath(recipe.id)}><span>{recipe.label}</span><small>{copy(`page.connected-work.module.direction.${directionKey}`)}</small></Link></li>;
              })}</ul>
            </article>
            {toolRecipe ? <article>
              <p className="apf-section-heading__eyebrow">{copy("page.connected-work.module.copilot-pattern-label")}</p>
              <h3>{toolRecipe.label}</h3>
              <p>{toolRecipe.outcome}</p>
              <dl className="apf-definition-list"><div><dt>{copy("page.connected-work.module.maturity-label")}</dt><dd><StatusMark value={businessModule.implementationStatus} /></dd></div><div><dt>{copy("page.connected-work.module.tool-proof-label")}</dt><dd><span className="apf-proof-mark apf-proof-mark--recipe">{copy("page.connected-work.proof.recipe")}</span></dd></div><div><dt>{copy("page.connected-work.module.action-class-label")}</dt><dd>{humanize(toolRecipe.actionClass)}</dd></div><div><dt>{copy("page.connected-work.module.authority-ceiling-label")}</dt><dd>{toolRecipe.authorityCeiling}</dd></div></dl>
              <div className="apf-module-tool-agents">
                <h4>{copy("page.connected-work.module.agent-ownership-label")}</h4>
                <p>{copy("page.connected-work.module.agent-ownership-description")}</p>
                <ul>{toolAgents.map((agent) => <li key={agent.id}><a href={`#agent-${agent.id}`}>{agent.label}</a></li>)}</ul>
              </div>
              <p><Link to={integrationRecipePath("workblock-mcp-tool-exposure")}>{copy("page.connected-work.module.open-mcp-recipe")}</Link></p>
            </article> : null}
          </div>
          <dl className="apf-recipe-facts apf-module-connection-facts">
            <div><dt>{copy("page.connected-work.module.representative-systems-label")}</dt><dd>{integrationBinding.representativeSystemIds.length ? integrationBinding.representativeSystemIds.map((id) => systemById.get(id)?.name ?? humanize(id)).join(" · ") : copy("page.connected-work.module.systems-to-resolve")}</dd></div>
            <div><dt>{copy("page.connected-work.module.sharepoint-label")}</dt><dd>{integrationBinding.sharePointPosture}</dd></div>
          </dl>
        </section>
      ) : null}

      <section className="apf-page-section" id="required">
        <SectionTitle eyebrow={copy("page.workblocks.module.required.eyebrow", "Data, systems and prerequisites")} title={copy("page.workblocks.module.required.title", "What it needs from your estate")} description={copy("page.workblocks.module.required.description", "Every record opens its definition, owner and authority contract; the strip below is drawn from the governed data mappings.")} />
        {records.length ? (
          <div className="apf-io-strip" aria-label={copy("page.workblocks.module.required.io-aria-label", "Records in, the decision, records out")}>
            <div className="apf-io-strip__column">
              <p className="apf-io-strip__label">{copy("page.workblocks.module.required.records-in", "Records in")}</p>
              <ul>{consumedRecords.map((entity) => <li key={entity.id}><Link to={groundworksEntityPath(entity.id)}>{entity.label}</Link></li>)}</ul>
            </div>
            <div className="apf-io-strip__column apf-io-strip__column--decision">
              <p className="apf-io-strip__label">{copy("page.workblocks.module.required.decision", "The decision")}</p>
              <p className="apf-io-strip__decision">{copy(businessModule.jobStatementKey)}</p>
            </div>
            <div className="apf-io-strip__column">
              <p className="apf-io-strip__label">{copy("page.workblocks.module.required.records-out", "Records out")}</p>
              <ul>{ownedRecords.map((entity) => <li key={entity.id}><Link to={groundworksEntityPath(entity.id)}>{entity.label}</Link></li>)}</ul>
            </div>
          </div>
        ) : <EmptyState title={copy("page.workblocks.module.required.empty-title", "No connected records")} description={copy("page.workblocks.module.required.empty-description", "This module has no projected GroundWorks record relationship.")} />}
        {businessModule.prerequisites?.length ? (
          <dl className="apf-definition-list apf-explainer-rows">
            <div><dt>{copy("page.workblocks.module.required.prerequisites", "Prerequisites")}</dt><dd><ul className="apf-explainer-list">{businessModule.prerequisites.map((item) => <li key={item}>{item}</li>)}</ul></dd></div>
          </dl>
        ) : null}
      </section>

      {businessModule.metrics.length ? (
        <section className="apf-page-section" id="measured">
          <SectionTitle eyebrow={copy("page.workblocks.module.measured.eyebrow", "KPIs and outcomes")} title={copy("page.workblocks.module.measured.title", "How you will know it worked")} description={copy("page.workblocks.module.measured.description", "Each measure keeps its definition and its drill-through to the records that produced it.")} />
          <dl className="apf-definition-list apf-explainer-rows">
            {businessModule.metrics.map((metric) => {
              const separator = metric.indexOf(":");
              const label = separator > 0 && separator < 60 ? metric.slice(0, separator).trim() : copy("page.workblocks.module.measured.default-label", "Measure");
              const body = separator > 0 && separator < 60 ? metric.slice(separator + 1).trim() : metric;
              return <div key={metric}><dt>{label}</dt><dd>{body}</dd></div>;
            })}
          </dl>
        </section>
      ) : null}

      {controlItems.length ? (
        <section className="apf-page-section" id="controls">
          <SectionTitle eyebrow={copy("page.workblocks.module.controls.eyebrow", "What could go wrong")} title={copy("page.workblocks.module.controls.title", "The failure paths, and what stops them")} description={copy("page.workblocks.module.controls.description", "These conditions stop the agent and hand the case to a person, every time.")} />
          <dl className="apf-definition-list apf-explainer-rows">
            <div><dt>{copy("page.workblocks.module.controls.always-escalates", "Always escalates on")}</dt><dd><ul className="apf-explainer-list">{controlItems.map((item) => <li key={item}>{item}</li>)}</ul></dd></div>
          </dl>
        </section>
      ) : null}

      <section className="apf-page-section" id="execution-flows">
        <SectionTitle eyebrow={copy("page.workblocks.module.flows.eyebrow", "Execution summary")} title={copy("page.workblocks.module.flows.title", "The flows that carry the job")} description={copy("page.workblocks.module.flows.description", "Each flow names its trigger, the approved subordinate jobs it serves and its completion evidence, without exposing implementation anatomy.")} />
        <div>
          {businessModule.executionFlows.map((flow) => {
            const flowStories = flow.jobIds.flatMap((jobId) => {
              const story = jobStoryById.get(jobId);
              return story ? [story] : [];
            });
            return (
              <details className="apf-disclosure" id={`flow-${flow.id}`} key={flow.id}>
                <summary>
                  <span className="apf-disclosure__title">{flow.label}</span>
                  <span className="apf-disclosure__meta">{humanize(flow.kind)}</span>
                </summary>
                <div className="apf-disclosure__body">
                  <dl className="apf-definition-list">
                    <div><dt>{copy("page.workblocks.module.flows.trigger", "Trigger")}</dt><dd>{flow.trigger}</dd></div>
                    {flowStories.length ? <div><dt>{copy("page.workblocks.module.flows.serves-jobs", "Serves the approved jobs")}</dt><dd><ul className="apf-explainer-list">{flowStories.map((story) => <li key={story.id}><a href="#job-stories">{copy(story.storyKey)}</a></li>)}</ul></dd></div> : null}
                    <div><dt>{copy("page.workblocks.module.flows.completion-evidence", "Completion evidence")}</dt><dd>{list(flow.jobToBeDone.completionEvidence)}</dd></div>
                  </dl>
                </div>
              </details>
            );
          })}
        </div>
      </section>

      <ModuleResourceShelf domainWorkblock={domainWorkblock} businessModule={businessModule} recipes={integrationRecipes} records={records} />

      <section className="apf-page-section" id="related">
        <SectionTitle eyebrow={copy("page.workblocks.module.related.eyebrow", "Where to go next")} title={copy("page.workblocks.module.related.title", "Related work")} description={copy("page.workblocks.module.related.description", "The business modules that share this Workblock, and the contract above them all.")} />
        <ol className="apf-explainer-systems">
          {siblingModules.map((sibling, index) => (
            <li key={sibling.id}>
              <Link className="apf-explainer-system" to={businessModulePath(domainWorkblock.id, sibling.id)}>
                <span className="apf-explainer-ordinal">0{index + 1}</span>
                <span className="apf-explainer-system__stage">{humanize(sibling.implementationStatus)}</span>
                <span className="apf-explainer-system__head"><span className="apf-explainer-system__name">{copy(sibling.labelKey)}</span></span>
                <span className="apf-explainer-system__description">{copy(sibling.jobStatementKey)}</span>
              </Link>
            </li>
          ))}
        </ol>
        <p className="apf-deep-link"><Link to={domainWorkblockPath(domainWorkblock.id)}><ArrowLinkLabel>{copy("page.workblocks.module.related.open-parent-contract", "Open the parent Workblock contract")}</ArrowLinkLabel></Link></p>
      </section>
    </div>
  );
}

/* The reading order of the editorial chapter: problem, story, jobs, people
 * and proof, then the technical contract and reusable material. */
const domainSections = [
  ["the-shift", "page.workblocks.domain.nav.the-shift", "The AI-first shift"],
  ["work-moment", "page.workblocks.domain.nav.work-moment", "Work moment"],
  ["business-modules", "page.workblocks.domain.nav.business-modules", "Business modules"],
  ["roles", "page.workblocks.domain.nav.roles", "People"],
  ["proof", "page.workblocks.domain.nav.proof", "Proof"],
  ["runs-alongside", "page.workblocks.domain.nav.runs-alongside", "Runs alongside"],
  ["workblock-contract", "page.workblocks.domain.nav.workblock-contract", "Contract"],
  ["connections", "page.connected-work.domain.nav", "Connected Work"],
  ["resources", "page.workblocks.domain.nav.resources", "Working material"]
] as const;

function DomainSectionNavigation({ present }: { present: ReadonlySet<string> }) {
  const copy = useCopy();
  const sections = domainSections.filter(([id]) => present.has(id));
  if (sections.length < 2) return null;
  return <nav className="apf-section-nav" aria-label={copy("page.workblocks.domain.nav.aria-label", "Sections on this Workblock")}>{sections.map(([id, labelKey, labelFallback]) => <a className="apf-section-nav__link" href={`#${id}`} key={id}>{copy(labelKey, labelFallback)}</a>)}</nav>;
}

export function domainGlyph(domainWorkblock: DomainWorkblock): GlyphMeaning | undefined {
  const short = domainWorkblock.labelKey.split(".")[1];
  const meaning = `domain.${short}`;
  return hasGlyph(meaning) ? meaning : undefined;
}

function RoleModuleLinks({ label, ids, moduleById }: { label: string; ids: readonly string[]; moduleById: ReadonlyMap<string, BusinessModule> }) {
  const copy = useCopy();
  const linked = ids.flatMap((id) => { const module = moduleById.get(id); return module ? [module] : []; });
  if (!linked.length) return null;
  return <div className="apf-role-card__fact"><dt>{label}</dt><dd>{linked.map((module, index) => <span key={module.id}>{index ? " · " : null}<Link to={businessModulePath(module.domainWorkblockId, module.id)}>{copy(module.labelKey)}</Link></span>)}</dd></div>;
}

function DomainRoleCard({ role, moduleById, brief, additionalFacts = [], expanded, onExpandedChange }: {
  role: DomainWorkblock["roleCards"][number];
  moduleById: ReadonlyMap<string, BusinessModule>;
  brief?: string;
  additionalFacts?: ReadonlyArray<readonly [label: string, messageKey: string | undefined]>;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) {
  const copy = useCopy();
  const facts: Array<readonly [string, string | undefined]> = [
    [copy("catalogue.preview.role.measured-on", "Measured on"), role.measuredKey],
    [copy("catalogue.preview.role.may-propose", "May propose"), role.proposeKey],
    [copy("catalogue.preview.role.may-commit", "May commit"), role.commitKey],
    [copy("catalogue.preview.role.never-alone", "Never alone"), role.aloneKey],
    [copy("catalogue.preview.role.hired-today", "Hired today"), role.hiredKey],
    ...additionalFacts
  ];
  return <li id={`role-${role.id}`}><ContextRecordPreview id={role.id} variant="role" heading={copy(role.roleKey)} summary={copy(role.mandateKey)} labels={{ showDetails: copy("component.disclosure.show-details", "Show details"), hideDetails: copy("component.disclosure.hide-details", "Hide details") }} metadata={[{ label: copy("catalogue.preview.role.arrival-question", "Arrival question"), value: copy(role.questionKey) }, { label: copy("catalogue.preview.role.related-jobs", "Related jobs"), value: String(new Set([...role.performsBusinessModuleIds, ...role.consultedBusinessModuleIds]).size) }]} expanded={expanded} onExpandedChange={onExpandedChange} to={rolePath(role.id)} canonicalLinkLabel={copy("catalogue.preview.role.open", "Open role record")} detail={<dl className="apf-role-card__facts">{brief ? <div className="apf-role-card__fact"><dt>{copy("catalogue.preview.role.drives", "Drives")}</dt><dd>{brief}</dd></div> : null}{facts.map(([label, key]) => key ? <div className="apf-role-card__fact" key={label}><dt>{label}</dt><dd>{copy(key)}</dd></div> : null)}<RoleModuleLinks label={copy("catalogue.preview.role.performs", "Performs")} ids={role.performsBusinessModuleIds} moduleById={moduleById} /><RoleModuleLinks label={copy("catalogue.preview.role.consulted-on", "Consulted on")} ids={role.consultedBusinessModuleIds} moduleById={moduleById} /></dl>} /></li>;
}

export interface DomainWorkblockExtensionContext {
  roleBriefById?: ReadonlyMap<string, string>;
  roleFactsById?: ReadonlyMap<string, ReadonlyArray<readonly [label: string, messageKey: string | undefined]>>;
}

/**
 * The flagship Work moment told as a narrative scene: a scene-setting lede
 * from the role's before/now copy, the decision trace drawn as an
 * illustration, the agent's contribution as a pull-quote, and the remaining
 * beats as readable story chapters. Every sentence renders from the governed
 * moment, fixture and agent records; the complete reference record remains
 * one link away and the illustrative and synthetic marks never leave the
 * frame.
 */
function DomainWorkMomentStory({ momentId, domainWorkblockId }: { momentId: string; domainWorkblockId: string }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const resolved = resolveWorkMoment(snapshot, momentId);
  if (!resolved) return null;
  const { definition, businessModule, perspective, agent, fixture } = resolved;
  const roles = roleDefinitions(snapshot);
  const roleLabel = (roleId: string) => {
    const role = roles.find(({ definition: candidate }) => candidate.id === roleId)?.definition;
    return role ? copy(role.labelKey) : humanize(roleId);
  };
  const superviseMode = fixture.modes["supervise-work"];
  const allRecords = Object.values(fixture.modes).flatMap((mode) => mode?.records ?? []);
  const exceptionRecord = allRecords.find(({ state }) => state === "exception");
  const committedRecord = allRecords.find(({ state }) => state === "committed");
  const reviewedRecord = allRecords.find(({ state }) => state === "reviewed");
  const beats: Array<{ id: string; title: string; body: ReactNode }> = [
    {
      id: "trigger",
      title: copy("work-moment.chapter.trigger", "Triggering signal"),
      body: <p>{superviseMode ? copy(superviseMode.summaryKey) : copy(definition.summaryKey)}{exceptionRecord ? <> {copy(exceptionRecord.detailKey)}</> : null}</p>
    },
    {
      id: "authority",
      title: copy("work-moment.chapter.authority", "Human authority"),
      body: <p>{copy(perspective.supervisionKey)} {copy("work-moment.frame.grantor", "Authority grantor")}: <Link to={rolePath(fixture.authorityGrantorRoleId)}>{roleLabel(fixture.authorityGrantorRoleId)}</Link>.</p>
    },
    ...(committedRecord ? [{
      id: "committed",
      title: copy("work-moment.chapter.committed", "Committed outcome"),
      body: <p>{copy(committedRecord.detailKey)}</p>
    }] : []),
    ...(reviewedRecord ? [{
      id: "learning",
      title: copy("work-moment.chapter.learning", "Reviewed learning"),
      body: <p>{copy(reviewedRecord.detailKey)}</p>
    }] : [])
  ];
  return (
    <section className="apf-page-section apf-domain-story apf-register-marketing" id="work-moment" aria-labelledby="work-moment-title">
      <p className="apf-proof-line"><span>{copy("work-moment.proof.illustrative", "Illustrative work moment")}</span><span>{copy("work-moment.proof.synthetic", "Synthetic data")}</span></p>
      <SectionTitle
        id="work-moment-title"
        eyebrow={`${copy("work-moment.chapter.eyebrow", "Work moment")} · ${copy(businessModule.labelKey)}`}
        title={copy(definition.titleKey)}
        description={copy(definition.summaryKey)}
      />
      <p className="apf-domain-story__lede">
        <Link to={rolePath(perspective.roleId)}>{roleLabel(perspective.roleId)}</Link> · {copy(perspective.responsibilityKey)} {copy(perspective.beforeKey)} {copy(perspective.nowKey)}
      </p>
      <WorkMomentIllustration momentId={definition.id} roleId={perspective.roleId} variant="compact" />
      <blockquote className="apf-domain-story__quote">
        <p>{copy(definition.agentContributionKey)}</p>
        <footer>{agent.label}</footer>
      </blockquote>
      <ol className="apf-domain-story__beats">
        {beats.map((beat) => (
          <li key={beat.id} className="apf-domain-story__beat">
            <h3>{beat.title}</h3>
            {beat.body}
          </li>
        ))}
      </ol>
      <p className="apf-deep-link">
        <Link to={workMomentPath(definition.id, perspective.roleId)}>
          <ArrowLinkLabel>{copy("work-moment.chapter.open-record", "Open the complete Work moment record")}</ArrowLinkLabel>
        </Link>
      </p>
      <p><Link to={`/reference/work-moments?domain=${encodeURIComponent(domainWorkblockId)}`}>{copy("work-moment.domain-preview.browse-all", "Browse work moments for this Workblock")}</Link></p>
    </section>
  );
}

/**
 * Governed lenses over this domain: every editorial story whose journey
 * passes through the domain renders as an editorial card, and a story that
 * declares a spend-segment binding also links the segment-filtered
 * catalogue. Everything renders from the projected story records; the
 * section disappears entirely for domains no story touches.
 */
function DomainLensSection({ domainWorkblockId }: { domainWorkblockId: string }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const stories = (snapshot.content.editorialStories ?? []).filter((story) =>
    story.domainWorkblockIds.includes(domainWorkblockId),
  );
  if (!stories.length) return null;
  return (
    <section
      className="apf-page-section apf-domain-lenses apf-register-marketing"
      id="lenses"
      aria-labelledby="domain-lenses-title"
    >
      <SectionTitle
        id="domain-lenses-title"
        eyebrow={copy("domain.lenses.eyebrow", "Cross-domain context")}
        title={copy("domain.lenses.title", "Lenses on this domain")}
        description={copy(
          "domain.lenses.description",
          "Cross-domain stories that pass through this system of work, each told from one spend perspective with named owners and governed records.",
        )}
      />
      <ul className="apf-domain-lenses__list">
        {stories.map((story) => (
          <li key={story.id} className="apf-domain-lenses__card">
            <p className="apf-domain-lenses__kicker">{copy(story.kickerKey)}</p>
            <h3 className="apf-domain-lenses__title">
              <Link to={`/client-engagement/${story.id}`}>{copy(story.titleKey)}</Link>
            </h3>
            <p className="apf-domain-lenses__summary">{copy(story.summaryKey)}</p>
            <p className="apf-deep-link">
              <Link to={`/client-engagement/${story.id}`}>
                <ArrowLinkLabel>
                  {copy("domain.lenses.read-story", "Read the cross-domain story")}
                </ArrowLinkLabel>
              </Link>
            </p>
            {story.spendSegmentId ? (
              <p className="apf-domain-lenses__catalogue">
                <Link to={`/workblocks?segment=${encodeURIComponent(story.spendSegmentId)}`}>
                  {`${copy("domain.lenses.catalogue-link", "Browse the segment-filtered catalogue")}: ${copy(
                    `dimension.spend-segment.member.${story.spendSegmentId}.label`,
                    humanize(story.spendSegmentId),
                  )}`}
                </Link>
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ResourceShelfRow({ asset, copy }: { asset: ManifestAsset; copy: (key: string, fallback?: string) => string }) {
  return (
    <li className="apf-domain-resources__row">
      <div className="apf-domain-resources__identity">
        <strong>{asset.title}</strong>
        <span>{copy("page.downloads.version-label")} {asset.version} · {copy("page.downloads.proof-label")}: {asset.proof.label}</span>
      </div>
      <div className="apf-domain-resources__actions">
        {asset.formats.map((format) => (
          <span key={format.path} className="apf-downloads-row__format">
            <a className="apf-button apf-button--quiet" href={assetHref(format.path)} download>
              {copy("page.downloads.download")} {format.format}
            </a>
            {copyableFormats.has(format.format) ? <CopyAssetButton path={format.path} copy={copy} /> : null}
          </span>
        ))}
        <Link to={downloadAssetPath(asset.assetId)}><ArrowLinkLabel>{copy("page.downloads.route-label", "Open canonical record")}</ArrowLinkLabel></Link>
      </div>
    </li>
  );
}

/**
 * The domain resource shelf lists only assets that exist in the generated
 * site-safe manifest; nothing here is fabricated on the page. When the
 * manifest cannot load, the shelf degrades to the canonical downloads link.
 */
function DomainResourceShelf({ domainWorkblock, businessModules, recipes }: {
  domainWorkblock: DomainWorkblock;
  businessModules: readonly BusinessModule[];
  recipes: ReadonlyArray<{ id: string; label: string }>;
}) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const { manifest, failed } = useAssetManifest();
  const assetById = new Map((manifest?.assets ?? []).map((asset) => [asset.assetId, asset]));
  const promptAsset = assetById.get(`workblock-prompt-${domainWorkblock.id}`);
  const starterAssets = (manifest?.assets ?? []).filter(
    (asset) => asset.assetClass === "workblock-starter" && asset.canonicalRoute.startsWith(`${domainWorkblockPath(domainWorkblock.id)}/`)
  );
  const recipeAssets = recipes.flatMap((recipe) => {
    const asset = assetById.get(`recipe-${recipe.id}`);
    return asset ? [asset] : [];
  });
  const entityAssets = snapshot.groundworks.entities
    .filter((entity) => entity.domainWorkblockIds.includes(domainWorkblock.id))
    .flatMap((entity) => {
      const asset = assetById.get(`entity-${entity.id}`);
      return asset ? [asset] : [];
    });
  return (
    <section className="apf-page-section apf-domain-resources" id="resources" aria-labelledby="domain-resources-title">
      <SectionTitle
        id="domain-resources-title"
        eyebrow={copy("page.downloads.eyebrow", "Governed resources")}
        title={copy("page.workblocks.resources.title", "Take this Workblock away as working material")}
        description={copy("page.workblocks.resources.description", "Each resource is generated from the published framework release with its version, proof class and licence recorded.")}
      />
      {failed ? <p className="apf-domain-resources__fallback">{copy("page.workblocks.resources.unavailable", "The resource catalogue is unavailable right now. The downloads page lists every governed asset.")}</p> : null}
      {manifest ? (
        <ul className="apf-domain-resources__list">
          {promptAsset ? <ResourceShelfRow asset={promptAsset} copy={copy} /> : null}
          {starterAssets.map((asset) => <ResourceShelfRow key={asset.assetId} asset={asset} copy={copy} />)}
          {recipeAssets.map((asset) => <ResourceShelfRow key={asset.assetId} asset={asset} copy={copy} />)}
        </ul>
      ) : null}
      <div className="apf-domain-resources__related">
        <h3>{copy("page.workblocks.resources.module-prompts", "Module design briefs")}</h3>
        <p>{businessModules.map((module, index) => <span key={module.id}>{index ? " · " : null}<Link to={businessModulePath(domainWorkblock.id, module.id)}>{copy(module.labelKey)}</Link></span>)}</p>
        {entityAssets.length ? (
          <>
            <h3>{copy("page.workblocks.resources.schemas", "Record schemas")}</h3>
            <p>{entityAssets.map((asset, index) => <span key={asset.assetId}>{index ? " · " : null}<Link to={downloadAssetPath(asset.assetId)}>{asset.title}</Link></span>)}</p>
          </>
        ) : null}
        <h3>{copy("page.workblocks.resources.components", "Design components")}</h3>
        <p><Link to="/design">{copy("page.workblocks.resources.components-link", "Inspect the design components this Workblock renders with")}</Link></p>
      </div>
      <p className="apf-deep-link"><Link to="/downloads"><ArrowLinkLabel>{copy("page.workblocks.resources.open-downloads", "Open the full resource catalogue")}</ArrowLinkLabel></Link></p>
    </section>
  );
}

/**
 * The module resource shelf mirrors the domain shelf: it lists only assets
 * present in the generated site-safe manifest and names the proof class each
 * control opens. When the manifest cannot load it degrades to the canonical
 * downloads link.
 */
function ModuleResourceShelf({ domainWorkblock, businessModule, recipes, records }: {
  domainWorkblock: DomainWorkblock;
  businessModule: BusinessModule;
  recipes: ReadonlyArray<{ id: string; label: string }>;
  records: ReadonlyArray<{ id: string; label: string }>;
}) {
  const copy = useCopy();
  const { manifest, failed } = useAssetManifest();
  const assetById = new Map((manifest?.assets ?? []).map((asset) => [asset.assetId, asset]));
  const briefAsset = assetById.get(`module-brief-${businessModule.id}`);
  const toolAsset = assetById.get(`outcome-tool-${businessModule.id}`);
  const charterAsset = assetById.get(`charter-${businessModule.id}`);
  const starterAssets = (manifest?.assets ?? []).filter(
    (asset) => asset.assetClass === "workblock-starter" && asset.source.objectId === businessModule.id
  );
  const recipeAssets = recipes.flatMap((recipe) => {
    const asset = assetById.get(`recipe-${recipe.id}`);
    return asset ? [asset] : [];
  });
  const entityAssets = records.flatMap((record) => {
    const asset = assetById.get(`entity-${record.id}`);
    return asset ? [asset] : [];
  });
  return (
    <section className="apf-page-section apf-domain-resources" id="resources" aria-labelledby="module-resources-title">
      <SectionTitle
        id="module-resources-title"
        eyebrow={copy("page.downloads.eyebrow", "Governed resources")}
        title={copy("page.workblocks.resources.module-title", "Take this module away as working material")}
        description={copy("page.workblocks.resources.description", "Each resource is generated from the published framework release with its version, proof class and licence recorded.")}
      />
      {failed ? <p className="apf-domain-resources__fallback">{copy("page.workblocks.resources.unavailable", "The resource catalogue is unavailable right now. The downloads page lists every governed asset.")}</p> : null}
      {manifest ? (
        <ul className="apf-domain-resources__list">
          {briefAsset ? <ResourceShelfRow asset={briefAsset} copy={copy} /> : null}
          {charterAsset ? <ResourceShelfRow asset={charterAsset} copy={copy} /> : null}
          {toolAsset ? <ResourceShelfRow asset={toolAsset} copy={copy} /> : null}
          {starterAssets.map((asset) => <ResourceShelfRow key={asset.assetId} asset={asset} copy={copy} />)}
          {recipeAssets.map((asset) => <ResourceShelfRow key={asset.assetId} asset={asset} copy={copy} />)}
        </ul>
      ) : null}
      <div className="apf-domain-resources__related">
        {entityAssets.length ? (
          <>
            <h3>{copy("page.workblocks.resources.schemas", "Record schemas")}</h3>
            <p>{entityAssets.map((asset, index) => <span key={asset.assetId}>{index ? " · " : null}<Link to={downloadAssetPath(asset.assetId)}>{asset.title}</Link></span>)}</p>
          </>
        ) : null}
        <h3>{copy("page.workblocks.resources.components", "Design components")}</h3>
        <p><Link to="/design">{copy("page.workblocks.resources.components-link", "Inspect the design components this Workblock renders with")}</Link></p>
      </div>
      <p className="apf-deep-link"><Link to={`/search?availability=reference&q=${encodeURIComponent(copy(businessModule.labelKey))}`}><ArrowLinkLabel>{copy("page.workblocks.resources.reference-builds", "Explore reference builds")}</ArrowLinkLabel></Link></p>
      <p className="apf-deep-link"><Link to="/downloads"><ArrowLinkLabel>{copy("page.workblocks.resources.open-downloads", "Open the full resource catalogue")}</ArrowLinkLabel></Link></p>
    </section>
  );
}

export function DomainWorkblockDetail({ domainWorkblock, action, extension, extensionContext }: { domainWorkblock: DomainWorkblock; action?: ReactNode; extension?: (domainWorkblock: DomainWorkblock) => ReactNode; extensionContext?: DomainWorkblockExtensionContext }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  usePageTitle(copy(domainWorkblock.nameKey));
  const location = useLocation();
  const returnState = workblocksCatalogueReturnState(location.state);
  const businessModules = businessModulesFor(snapshot, domainWorkblock.id);
  const counts = businessModules.reduce<Record<string, number>>((result, module) => {
    result[module.implementationStatus] = (result[module.implementationStatus] ?? 0) + 1;
    return result;
  }, {});
  const statusLabels = new Map<string, string>(snapshot.content.businessModuleStatusDisplay.map((status) => [status.id, copy(status.chipKey)]));
  const entityCount = snapshot.groundworks.entities.filter((entity) => entity.domainWorkblockIds.includes(domainWorkblock.id)).length;
  const moduleById = new Map(businessModules.map((module) => [module.id, module]));
  const canonicalRoleById = new Map(roleDefinitions(snapshot).map(({ definition }) => [definition.id, definition]));
  const narratives = domainWorkblock.narrativeGapKeys ?? [];
  const changes = domainWorkblock.changes ?? [];
  const principles = domainWorkblock.deepDivePrinciples ?? [];
  const landscapes = snapshot.content.landscapes.filter((entry) => entry.domainWorkblockIds.includes(domainWorkblock.id));
  const capabilityMappings = partnerCapabilityLinksForTarget(snapshot, copy, "domain-workblock", domainWorkblock.id);
  const domainBindings = snapshot.integrations.moduleBindings.filter((binding) => domainWorkblock.businessModuleIds.includes(binding.businessModuleId));
  const domainRecipeIds = new Set(domainBindings.flatMap((binding) => [...binding.inboundRecipeIds, ...binding.outboundRecipeIds]));
  const domainRecipes = snapshot.integrations.recipes.filter((recipe) => domainRecipeIds.has(recipe.id));
  const featuredExample = snapshot.integrations.examples.find((example) => example.domainWorkblockId === domainWorkblock.id && example.toolRecipeId);
  const present = new Set<string>(["business-modules", "workblock-contract", "roles", "proof", "connections", "runs-alongside", "resources"]);
  const orderedDomainIds = [...(snapshot.content.stages ?? [])].sort((a, b) => a.order - b.order).flatMap((stage) => stage.domainWorkblockIds);
  const nextDomainId = orderedDomainIds[(orderedDomainIds.indexOf(domainWorkblock.id) + 1) % Math.max(orderedDomainIds.length, 1)];
  const nextDomain = nextDomainId && nextDomainId !== domainWorkblock.id ? snapshot.content.domainWorkblocks.find(({ id }) => id === nextDomainId) : undefined;
  const [expandedPreview, setExpandedPreview] = useState<string | undefined>();
  const previewRoles = (domainWorkblock.previewRoleIds?.length ? domainWorkblock.previewRoleIds : domainWorkblock.roleCards.slice(0, 3).map(({ id }) => id)).flatMap((id) => { const role = domainWorkblock.roleCards.find((candidate) => candidate.id === id); return role ? [role] : []; }).slice(0, 3);
  const previewJobs = (domainWorkblock.previewBusinessModuleIds?.length ? domainWorkblock.previewBusinessModuleIds : businessModules.slice(0, 3).map(({ id }) => id)).flatMap((id) => { const module = moduleById.get(id); return module ? [module] : []; }).slice(0, 3);
  const flagshipMoment = workMomentsForDomain(snapshot, domainWorkblock.id)[0];
  if (domainWorkblock.buyerQuestionKey || narratives.length || changes.length) present.add("the-shift");
  if (flagshipMoment) present.add("work-moment");

  return (
    <div className="apf-page apf-register-marketing">
      <PlaceTrail stops={[
        { label: copy("component.place-trail.framework", "Framework"), to: "/" },
        { label: copy("nav.workblocks", "Workblocks"), to: returnState?.returnTo ?? "/workblocks" },
        { label: copy(domainWorkblock.nameKey) }
      ]} />
      <header className="apf-domain-hero apf-domain-hero--plane">
        {domainGlyph(domainWorkblock) ? <Glyph meaning={domainGlyph(domainWorkblock)!} size={24} className="apf-domain-hero__glyph" /> : null}
        <p className="apf-page-header__eyebrow">{copy("page.workblocks.domain.eyebrow", "Domain Workblock")} · {copy(domainWorkblock.labelKey)}</p>
        <h1>{copy(domainWorkblock.nameKey)}</h1>
        {domainWorkblock.signatureKey ? <p className="apf-system-signature apf-system-signature--plane">{copy(domainWorkblock.signatureKey)}</p> : null}
        <p className="apf-detail-hero__descriptor">{copy(domainWorkblock.descriptorKey)}</p>
        {/* The outcome sentence has one placement: the AI-first-shift plane
          * restates it as the composed headline, so the hero only carries it
          * when that plane is absent. */}
        {present.has("the-shift") ? null : <p className="apf-domain-hero__outcome">{copy(domainWorkblock.outcomeKey)}</p>}
        <FactRow facts={[
          { value: businessModules.length, label: copy("page.workblocks.domain.fact.modules", "Business modules"), to: "#business-modules" },
          { value: businessModules.reduce((total, module) => total + module.executionFlows.length, 0), label: copy("page.workblocks.domain.fact.flows", "Execution flows"), to: "#business-modules" },
          { value: businessModules.reduce((total, module) => total + module.agents.length, 0), label: copy("page.workblocks.domain.fact.agents", "Governed agents"), to: "#business-modules" }
        ]} />
        {/* The status distribution is a fourth reading FactRow's three-fact
          * contract cannot carry, so it takes its own quiet line rather than
          * being silently dropped. */}
        <p className="apf-domain-hero__module-status">
          <Link to="#business-modules">
            {Object.entries(counts).map(([status, count]) => (
              <span key={status} className="apf-domain-hero__status-chip">
                <strong>{count}</strong> {statusLabels.get(status) ?? humanize(status)}
              </span>
            ))}
          </Link>
        </p>
        {action ? <div className="apf-detail-actions">{action}</div> : null}
      </header>

      <DomainSectionNavigation present={present} />

      {present.has("the-shift") ? <StatementSection id="the-shift" eyebrow={copy("page.workblocks.domain.shift.eyebrow", "The AI-first shift")} headline={copy(domainWorkblock.outcomeKey)}>
        {domainWorkblock.buyerQuestionKey ? <p className="apf-domain-question">{copy(domainWorkblock.buyerQuestionKey)}</p> : null}
        {narratives.length ? <div className="apf-domain-narrative">{narratives.map((key) => <p key={key}>{copy(key)}</p>)}</div> : null}
      </StatementSection> : null}

      {flagshipMoment ? <DomainWorkMomentStory momentId={flagshipMoment.id} domainWorkblockId={domainWorkblock.id} /> : null}

      <DomainLensSection domainWorkblockId={domainWorkblock.id} />

      <section className="apf-page-section apf-band apf-band--tint" id="business-modules" aria-labelledby="business-modules-title">
        <SectionTitle
          id="business-modules-title"
          eyebrow={copy("page.workblocks.domain.modules.eyebrow", "Five bounded outcomes")}
          title={copy("page.workblocks.domain.modules.title", "Five business modules carry the whole outcome")}
          description={copy("page.workblocks.domain.modules.description", "Each module keeps its own features, execution flows, tasks, records, authority, learning loop and roadmap.")}
        />
        <StatusLegend />
        <ol className="apf-ruled-index apf-ruled-index--workblocks">
          {businessModules.map((businessModule, index) => {
            const descriptor = copy(businessModule.descriptorKey);
            const label = copy(businessModule.labelKey);
            return <BusinessModuleIndexRow key={businessModule.id} businessModule={businessModule} position={index + 1} kicker={descriptor === label ? undefined : descriptor} lead={copy(businessModule.jobStatementKey)} />;
          })}
        </ol>
      </section>

      {domainWorkblock.roleCards.length ? <section className="apf-page-section" id="roles"><SectionHeading glyph="class.role" title={copy("catalogue.preview.title", "People and progress")} description={copy("catalogue.preview.description", "A bounded preview keeps this Workblock readable. The complete role and job catalogues remain one link away.")} /><div className="apf-context-preview-groups"><div><h3>{copy("catalogue.preview.roles.title", "People in this Workblock")}</h3><p>{replaceCount(copy("catalogue.preview.roles.count", "{count} roles in the complete catalogue"), domainWorkblock.roleCards.length)}</p><ul className="apf-context-preview-list">{previewRoles.map((role) => <DomainRoleCard key={role.id} role={role} moduleById={moduleById} brief={extensionContext?.roleBriefById?.get(role.id)} additionalFacts={extensionContext?.roleFactsById?.get(role.id)} expanded={expandedPreview === `role:${role.id}`} onExpandedChange={(expanded) => setExpandedPreview(expanded ? `role:${role.id}` : undefined)} />)}</ul><p><Link to={`/reference/roles?domain=${encodeURIComponent(domainWorkblock.id)}`}>{copy("catalogue.preview.roles.browse-all", "Browse all roles in this Workblock")}</Link></p></div><div><h3>{copy("catalogue.preview.jobs.title", "Jobs this Workblock owns")}</h3><p>{replaceCount(copy("catalogue.preview.jobs.count", "{count} jobs in the complete catalogue"), businessModules.length)}</p><ul className="apf-context-preview-list">{previewJobs.map((module) => <li key={module.id}><ContextRecordPreview id={module.id} variant="job" heading={copy(module.labelKey)} summary={copy(module.jobStatementKey)} labels={{ showDetails: copy("component.disclosure.show-details", "Show details"), hideDetails: copy("component.disclosure.hide-details", "Hide details") }} metadata={[{ label: copy("catalogue.preview.job.status", "Status"), value: statusLabels.get(module.implementationStatus) ?? humanize(module.implementationStatus) }, { label: copy("catalogue.preview.job.situations", "Situations"), value: String(module.jobStories.length) }]} expanded={expandedPreview === `job:${module.id}`} onExpandedChange={(expanded) => setExpandedPreview(expanded ? `job:${module.id}` : undefined)} to={businessModulePath(domainWorkblock.id, module.id)} canonicalLinkLabel={copy("catalogue.preview.job.open", "Open job record")} detail={<><p><strong>{copy("catalogue.preview.job.current-substitute", "Current substitute")}:</strong> {copy(module.hiredInsteadKey)}</p>{module.jobStories.slice(0, 2).map((story) => <p key={story.id}>{copy(story.storyKey)}</p>)}</>} /></li>)}</ul><p><Link to={`/reference/jobs?domain=${encodeURIComponent(domainWorkblock.id)}`}>{copy("catalogue.preview.jobs.browse-all", "Browse all jobs in this Workblock")}</Link></p></div></div>{changes.length ? <div className="apf-change-list">{changes.map((change) => <BeforeAfter key={change.id} title={copy(change.titleKey)} beforeLabel={copy("page.workblocks.domain.change.before", "Before")} afterLabel={copy("page.workblocks.domain.change.after", "After")} before={copy(change.beforeKey)} after={copy(change.afterKey)} />)}</div> : null}</section> : null}

      <section className="apf-page-section" id="proof">
        <SectionHeading title={copy("page.workblocks.domain.proof.title", "Start small, prove it on one case")} description={copy("page.workblocks.domain.proof.description", "Start with a bounded outcome, then test whether records, authority and evidence remain continuous across the five modules.")} />
        {domainWorkblock.wedgeKey || domainWorkblock.proofQuestionKey ? <div className="apf-wedge-pair">{domainWorkblock.wedgeKey ? <article className="apf-wedge-pair__item"><p className="apf-wedge-pair__kicker">{copy("page.workblocks.domain.proof.start-here", "Start here")}</p><p>{copy(domainWorkblock.wedgeKey)}</p></article> : null}{domainWorkblock.proofQuestionKey ? <article className="apf-wedge-pair__item"><p className="apf-wedge-pair__kicker">{copy("page.workblocks.domain.proof.question-kicker", "The proof question")}</p><p className="apf-wedge-pair__question">{copy(domainWorkblock.proofQuestionKey)}</p></article> : null}</div> : null}
        {principles.length ? <dl className="apf-definition-list apf-domain-contract">{principles.map((principle) => <div key={principle.id}><dt>{copy(principle.titleKey)}</dt><dd>{copy(principle.bodyKey)}</dd></div>)}</dl> : null}
        <p className="apf-deep-link"><Link to={`/groundworks?section=entities&domain=${domainWorkblock.id}`}><ArrowLinkLabel>{replaceCount(copy("page.workblocks.domain.proof.open-records", "Open {count} connected records, ownership and exact field contracts"), entityCount)}</ArrowLinkLabel></Link></p>
      </section>

      <section className="apf-page-section" id="runs-alongside" aria-label={copy("page.workblocks.domain.coexistence.aria-label", "Runs alongside")}>
        <SectionHeading glyph="class.coexistence" title={copy("page.workblocks.domain.coexistence.title", "It runs beside what you already own")} />
        <p className="apf-coexist-line">{copy("adoption.motion")} {copy("adoption.keep")}</p>
        {landscapes.length ? <p className="apf-coexist-links">{landscapes.map((entry, index) => <span key={entry.id}>{index ? " · " : null}<Link to={`/architecture#${entry.id}`}>{copy(entry.labelKey)}</Link></span>)}</p> : null}
        {extension?.(domainWorkblock)}
      </section>

      <section className="apf-page-section" id="workblock-contract" aria-labelledby="workblock-contract-title">
        <SectionTitle id="workblock-contract-title" eyebrow={replaceTokens(copy("page.workblocks.domain.contract.eyebrow", "Workblock contract · v{version}"), { version: domainWorkblock.version })} title={copy("page.workblocks.domain.contract.title", "One contract governs everything the Workblock does")} description={copy("page.workblocks.domain.contract.description", "The parent owns the cross-module exchange; modules retain their bounded implementation contracts.")} />
        <dl className="apf-definition-list apf-surface apf-workblock-contract">
          <div><dt>{copy("page.workblocks.domain.contract.accountable-roles", "Accountable roles")}</dt><dd>{domainWorkblock.workblockContract.accountableRoleIds.map((id, index) => { const role = canonicalRoleById.get(id); return <span key={id}>{index ? " · " : null}<Link data-role-id={id} to={rolePath(id)}>{role ? copy(role.labelKey) : id}</Link></span>; })}</dd></div>
          <div><dt>{copy("page.workblocks.domain.contract.trigger", "Trigger")}</dt><dd>{domainWorkblock.workblockContract.trigger}</dd></div>
          <div><dt>{copy("page.workblocks.domain.contract.completion-evidence", "Completion evidence")}</dt><dd>{list(domainWorkblock.workblockContract.completionEvidence)}</dd></div>
          <div><dt>{copy("page.workblocks.domain.contract.failure-consequence", "Failure consequence")}</dt><dd>{domainWorkblock.workblockContract.failureConsequence}</dd></div>
          <div><dt>{copy("page.workblocks.domain.contract.authority", "Authority")}</dt><dd>{domainWorkblock.workblockContract.authority}</dd></div>
          <div><dt>{copy("page.workblocks.domain.contract.ux-boundary", "UX boundary")}</dt><dd>{domainWorkblock.workblockContract.ux}</dd></div>
          <div><dt>{copy("page.workblocks.domain.contract.release-boundary", "Release boundary")}</dt><dd>{domainWorkblock.workblockContract.releaseBoundary}</dd></div>
          <div><dt>{copy("page.workblocks.domain.contract.cross-module-orchestration", "Cross-module orchestration")}</dt><dd>{domainWorkblock.workblockContract.crossModuleOrchestration}</dd></div>
        </dl>
        <CapabilityMapping
          label={copy("partner-capability.detail.heading")}
          items={capabilityMappings}
        />
      </section>

      <section className="apf-page-section" id="connections">
        <SectionHeading glyph="capability.integration" title={copy("page.connected-work.domain.title")} description={copy("page.connected-work.domain.description")} />
        {/* A zero reading is a defect, not a fact: only non-zero counts render,
          * each with its number-agreed label. */}
        {domainRecipes.length || domainBindings.length ? (
          <div className="apf-domain-connection-line">
            {domainRecipes.length ? <div><strong>{domainRecipes.length}</strong><span>{copy(domainRecipes.length === 1 ? "page.connected-work.domain.recipe-count-label.singular" : "page.connected-work.domain.recipe-count-label")}</span></div> : null}
            {domainBindings.length ? <div><strong>{domainBindings.length}</strong><span>{copy(domainBindings.length === 1 ? "page.connected-work.domain.mapping-count-label.singular" : "page.connected-work.domain.mapping-count-label")}</span></div> : null}
            {domainBindings.length ? <div><strong>{domainBindings.length}</strong><span>{copy(domainBindings.length === 1 ? "page.connected-work.domain.tool-count-label.singular" : "page.connected-work.domain.tool-count-label")}</span></div> : null}
          </div>
        ) : null}
        <ul className="apf-domain-recipe-list">{domainRecipes.map((recipe) => <li key={recipe.id}><Link to={integrationRecipePath(recipe.id)}><span className="apf-proof-mark apf-proof-mark--recipe">{copy("page.connected-work.proof.recipe")}</span><strong>{recipe.label}</strong><span>{copy(`page.connected-work.recipe-direction.${recipe.direction}`)}</span></Link></li>)}</ul>
        {featuredExample ? <article className="apf-domain-copilot-example"><div><p className="apf-section-heading__eyebrow">{copy("page.connected-work.domain.copilot-example-label")}</p><h3>{featuredExample.label}</h3><p>{featuredExample.summary}</p></div><div><span className={`apf-proof-mark apf-proof-mark--${featuredExample.proofClass}`}>{copy(`page.connected-work.proof.${featuredExample.proofClass}`)}</span><Link to={integrationExamplePath(featuredExample.id)}>{copy("page.connected-work.domain.open-example")}</Link></div></article> : null}
      </section>

      <DomainResourceShelf domainWorkblock={domainWorkblock} businessModules={businessModules} recipes={domainRecipes} />

      {nextDomain ? (
        <p className="apf-deep-link">
          <Link to={domainWorkblockPath(nextDomain.id)}>
            <ArrowLinkLabel>{replaceLabel(copy("page.workblocks.domain.next", "Next: {label}"), copy(nextDomain.nameKey))}</ArrowLinkLabel>
          </Link>
        </p>
      ) : null}
    </div>
  );
}

export function WorkblocksPageBase({
  domainAction,
  domainExtension,
  extensionContext
}: {
  domainAction?: (domainWorkblock: DomainWorkblock) => ReactNode;
  domainExtension?: (domainWorkblock: DomainWorkblock) => ReactNode;
  extensionContext?: DomainWorkblockExtensionContext;
} = {}) {
  const { domainWorkblockId, businessModuleId } = useParams();
  const { snapshot } = useFramework();
  const copy = useCopy();
  const { stageByDomainWorkblock } = useFrameworkModel();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [facetOpen, setFacetOpenState] = useState<Record<WorkblockFacetKey, boolean>>({
    role: false,
    job: false,
    segment: false,
    buying: false,
    treatment: false
  });
  const facetBaselineRef = useRef<Partial<Record<WorkblockFacetKey, string>>>({});
  const searchBaselineRef = useRef<string | undefined>(undefined);
  const catalogueSearchRef = useRef<HTMLInputElement>(null);

  const domainWorkblock = domainWorkblockId
    ? snapshot.content.domainWorkblocks.find(({ id }) => id === domainWorkblockId)
    : undefined;
  const businessModule = businessModuleId
    ? snapshot.businessModuleRegistry.businessModules.find(({ id }) => id === businessModuleId)
    : undefined;

  const roleOptions = useMemo(() => roleDefinitions(snapshot).map(({ definition, placements }) => ({ id: definition.id, label: copy(definition.labelKey), context: replaceCount(copy(placements.length === 1 ? "catalogue.workblocks.role-context.singular" : "catalogue.workblocks.role-context.plural", placements.length === 1 ? "{count} domain" : "{count} domains"), placements.length), count: placements.length })), [copy, snapshot]);
  const jobOptions = useMemo(() => snapshot.businessModuleRegistry.businessModules.map((module) => ({ id: module.id, label: copy(module.labelKey), context: copy(snapshot.content.domainWorkblocks.find(({ id }) => id === module.domainWorkblockId)?.nameKey ?? module.domainWorkblockId), count: 1 })), [copy, snapshot]);
  const activeDimensionFacetDefinitions = useMemo(
    () => activeWorkblockDimensionFacetDefinitions(snapshot),
    [snapshot]
  );
  const activeDimensionFacetIds = useMemo(
    () => new Set(activeDimensionFacetDefinitions.map(({ dimensionId }) => dimensionId)),
    [activeDimensionFacetDefinitions]
  );
  const dimensionOptions = useMemo(() => Object.fromEntries(
    workblockDimensionFacetDefinitions.map((facet) => {
      if (!activeDimensionFacetIds.has(facet.dimensionId)) return [facet.param, []];
      const counts = new Map<string, number>();
      for (const module of snapshot.businessModuleRegistry.businessModules) {
        for (const memberId of workblockDimensionMemberIds(module, facet.dimensionId)) {
          counts.set(memberId, (counts.get(memberId) ?? 0) + 1);
        }
      }
      const orderedMemberIds = [
        ...facet.memberOrder.filter((memberId) => counts.has(memberId)),
        ...[...counts.keys()].filter((memberId) => !facet.memberOrder.includes(memberId)).sort()
      ];
      return [facet.param, orderedMemberIds.map((id) => {
        const count = counts.get(id) ?? 0;
        return {
          id,
          label: dimensionMemberLabel(copy, facet.param, id),
          context: replaceCount(
            copy(
              count === 1
                ? "catalogue.workblocks.dimension-context.singular"
                : "catalogue.workblocks.dimension-context.plural",
              count === 1 ? "{count} job" : "{count} jobs"
            ),
            count
          ),
          count
        };
      })];
    })
  ) as Record<WorkblockDimensionFacetParam, Array<{ id: string; label: string; context: string; count: number }>>, [activeDimensionFacetIds, copy, snapshot]);
  const roleCatalogueIds = useMemo(() => roleOptions.map(({ id }) => id), [roleOptions]);
  const jobCatalogueIds = useMemo(() => jobOptions.map(({ id }) => id), [jobOptions]);
  const dimensionCatalogueIds = useMemo(() => Object.fromEntries(
    workblockDimensionFacetDefinitions.map(({ param }) => [param, dimensionOptions[param].map(({ id }) => id)])
  ) as Record<WorkblockDimensionFacetParam, string[]>, [dimensionOptions]);
  const roleLabelById = useMemo(() => new Map(roleOptions.map(({ id, label }) => [id, label])), [roleOptions]);
  const jobById = useMemo(() => new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module])), [snapshot]);
  const canonicalSearchParams = useMemo(
    () => canonicalWorkblockSearchParams(searchParams, roleCatalogueIds, jobCatalogueIds, dimensionCatalogueIds),
    [searchParams, roleCatalogueIds, jobCatalogueIds, dimensionCatalogueIds]
  );
  const canonicalSearch = searchString(canonicalSearchParams);
  const selectedRoleIds = canonicalizeFacetIds(searchParams.getAll("role"), roleCatalogueIds);
  const selectedJobIds = canonicalizeFacetIds(searchParams.getAll("job"), jobCatalogueIds);
  const selectedDimensionIds = useMemo(() => Object.fromEntries(
    workblockDimensionFacetDefinitions.map(({ param }) => [
      param,
      canonicalizeFacetIds(searchParams.getAll(param), dimensionCatalogueIds[param])
    ])
  ) as Record<WorkblockDimensionFacetParam, string[]>, [dimensionCatalogueIds, searchParams]);
  const validRoleIds = new Set(roleCatalogueIds);
  const validJobIds = new Set(jobCatalogueIds);
  const validDimensionIds = useMemo(() => Object.fromEntries(
    workblockDimensionFacetDefinitions.map(({ param }) => [param, new Set(dimensionCatalogueIds[param])])
  ) as Record<WorkblockDimensionFacetParam, Set<string>>, [dimensionCatalogueIds]);
  const availableRoleIds = selectedRoleIds.filter((id) => validRoleIds.has(id));
  const availableJobIds = selectedJobIds.filter((id) => validJobIds.has(id));
  const availableDimensionIds = useMemo(() => Object.fromEntries(
    workblockDimensionFacetDefinitions.map(({ param }) => [
      param,
      selectedDimensionIds[param].filter((id) => validDimensionIds[param].has(id))
    ])
  ) as Record<WorkblockDimensionFacetParam, string[]>, [selectedDimensionIds, validDimensionIds]);
  const repeatedFacetCatalogueIds: Record<WorkblockFacetKey, readonly string[]> = {
    role: roleCatalogueIds,
    job: jobCatalogueIds,
    segment: dimensionCatalogueIds.segment,
    buying: dimensionCatalogueIds.buying,
    treatment: dimensionCatalogueIds.treatment
  };
  const selectedFacetIds: Record<WorkblockFacetKey, readonly string[]> = {
    role: selectedRoleIds,
    job: selectedJobIds,
    segment: selectedDimensionIds.segment,
    buying: selectedDimensionIds.buying,
    treatment: selectedDimensionIds.treatment
  };
  const availableFacetIds: Record<WorkblockFacetKey, readonly string[]> = {
    role: availableRoleIds,
    job: availableJobIds,
    segment: availableDimensionIds.segment,
    buying: availableDimensionIds.buying,
    treatment: availableDimensionIds.treatment
  };
  const unavailableCount = workblockFacetKeys.reduce(
    (total, key) => total + selectedFacetIds[key].length - availableFacetIds[key].length,
    0
  );
  const dimensionSelectionSignature = workblockDimensionFacetDefinitions
    .map(({ param }) => selectedDimensionIds[param].join("\u0000"))
    .join("\u0001");
  const rows = useMemo(
    () => workblockMatchesFacets(snapshot, {
      query,
      roleIds: availableRoleIds,
      jobIds: availableJobIds,
      dimensionMemberIds: availableDimensionIds
    }),
    [availableDimensionIds, availableJobIds, availableRoleIds, dimensionSelectionSignature, query, snapshot, selectedJobIds.join("\u0000"), selectedRoleIds.join("\u0000")]
  );

  useEffect(() => {
    if (!domainWorkblockId && !businessModuleId && location.search !== canonicalSearch) {
      navigate({ pathname: location.pathname, search: canonicalSearch, hash: location.hash }, { replace: true, preventScrollReset: true });
    }
  }, [businessModuleId, canonicalSearch, domainWorkblockId, location.hash, location.pathname, location.search, navigate]);

  const replaceSearchParams = (next: URLSearchParams) => {
    setSearchParams(canonicalWorkblockSearchParams(next, roleCatalogueIds, jobCatalogueIds, dimensionCatalogueIds), {
      replace: true,
      preventScrollReset: true
    });
  };

  const commitReplacedSearch = (baseline: string | undefined) => {
    if (baseline === undefined || baseline === canonicalSearch) return;
    const destination = { pathname: location.pathname, hash: location.hash };
    navigate({ ...destination, search: baseline }, { replace: true, preventScrollReset: true });
    navigate({ ...destination, search: canonicalSearch }, { preventScrollReset: true });
  };

  const setFacetOpen = (facet: WorkblockFacetKey, open: boolean) => {
    if (open) {
      facetBaselineRef.current[facet] = canonicalSearch;
    } else {
      const baseline = facetBaselineRef.current[facet];
      delete facetBaselineRef.current[facet];
      commitReplacedSearch(baseline);
    }
    setFacetOpenState((current) => ({ ...current, [facet]: open }));
  };

  const updateRepeated = (key: WorkblockFacetKey, ids: readonly string[]) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const facet of workblockFacetKeys) next.delete(facet);
      for (const facet of workblockFacetKeys) {
        const candidateIds = key === facet ? ids : current.getAll(facet);
        for (const id of canonicalizeFacetIds(candidateIds, repeatedFacetCatalogueIds[facet])) {
          next.append(facet, id);
        }
      }
      return canonicalWorkblockSearchParams(next, roleCatalogueIds, jobCatalogueIds, dimensionCatalogueIds);
    }, { replace: true, preventScrollReset: true });
  };
  const clearAll = () => {
    searchBaselineRef.current = undefined;
    facetBaselineRef.current = {};
    setSearchParams({}, { preventScrollReset: true });
    catalogueSearchRef.current?.focus();
  };
  const clearUnavailableFacet = (facet: WorkblockFacetKey) => {
    updateRepeated(facet, availableFacetIds[facet]);
    catalogueSearchRef.current?.focus();
  };
  const clearUnavailable = () => {
    const next = new URLSearchParams(searchParams);
    for (const facet of workblockFacetKeys) next.delete(facet);
    for (const facet of workblockFacetKeys) {
      for (const id of availableFacetIds[facet]) next.append(facet, id);
    }
    setSearchParams(canonicalWorkblockSearchParams(next, roleCatalogueIds, jobCatalogueIds, dimensionCatalogueIds), { preventScrollReset: true });
    catalogueSearchRef.current?.focus();
  };
  const submitSearch = () => {
    const baseline = searchBaselineRef.current;
    searchBaselineRef.current = undefined;
    commitReplacedSearch(baseline);
  };
  const facetLabels = {
    search: (label: string) => replaceLabel(copy("component.facet.search", "Search {label}"), label),
    clear: (label: string) => replaceLabel(copy("component.facet.clear", "Clear {label}"), label),
    clearAll: copy("component.facet.clear-all", "Clear all"),
    selectedCount: (count: number) => replaceCount(copy("component.facet.selected-count", "{count} selected"), count),
    moreCount: (count: number) => replaceCount(copy("component.facet.more-count", "{count} more"), count),
    noOptions: copy("component.facet.no-options", "No matching options."),
    tryAgain: copy("component.facet.try-again", "Try again."),
    resultCount: (count: number) => replaceCount(copy("component.facet.result-count", "{count} options match."), count),
    optionCount: (count: number) => replaceCount(copy("component.facet.option-count", "{count} matching records"), count),
    unavailableCount: (count: number) => replaceCount(copy("component.facet.unavailable-count", "{count} filters are unavailable in this view."), count),
    clearUnavailable: copy("component.facet.clear-unavailable", "Clear unavailable filters")
  };

  if (domainWorkblockId && !domainWorkblock) return <div className="apf-page apf-register-reference"><AbsenceBlock eyebrow={copy("absence.workblock.eyebrow", "Workblock reference")} title={copy("absence.workblock.title", "Workblock not found")} support={copy("absence.workblock.support", "This Workblock is not part of the current framework.")} headingLevel={1} holds={[{ label: copy("absence.holding.workblocks", "Domain Workblocks"), count: snapshot.content.domainWorkblocks.length }, { label: copy("absence.holding.business-modules", "Business modules"), count: jobOptions.length }, { label: copy("absence.holding.roles", "Roles"), count: roleOptions.length }]} routes={<Link to="/workblocks">{copy("absence.workblock.route", "Browse Workblocks")}</Link>} /></div>;
  if (businessModuleId && (!businessModule || businessModule.domainWorkblockId !== domainWorkblockId || !domainWorkblock)) return <div className="apf-page apf-register-reference"><AbsenceBlock eyebrow={copy("absence.business-module.eyebrow", "Business module reference")} title={copy("absence.business-module.title", "Business module not found")} support={copy("absence.business-module.support", "This business module does not belong to the requested Workblock.")} headingLevel={1} holds={[{ label: copy("absence.holding.business-modules", "Business modules"), count: jobOptions.length }, { label: copy("absence.holding.workblocks", "Domain Workblocks"), count: snapshot.content.domainWorkblocks.length }, { label: copy("absence.holding.roles", "Roles"), count: roleOptions.length }]} routes={<Link to={domainWorkblock ? domainWorkblockPath(domainWorkblock.id) : "/workblocks"}>{copy(domainWorkblock ? "absence.business-module.route-domain" : "absence.business-module.route-workblocks", domainWorkblock ? "Return to the parent Workblock" : "Browse Workblocks")}</Link>} /></div>;
  if (domainWorkblock && businessModule) {
    return <PublishedBusinessModuleDetail domainWorkblock={domainWorkblock} businessModule={businessModule} />;
  }
  if (domainWorkblock) return <DomainWorkblockDetail domainWorkblock={domainWorkblock} action={domainAction?.(domainWorkblock)} extension={domainExtension} extensionContext={extensionContext} />;

  return (
    <div className="apf-page apf-register-reference apf-workblocks-page">
      <PageHeader eyebrow={copy("page.workblocks.eyebrow", "Workblocks")} title={copy("page.workblocks.title", "Nine Workblocks. One source-to-pay foundation.")} description={copy("page.workblocks.description", "Each domain Workblock is a coherent system of work composed from five bounded business modules.")} meta={<span>{replaceValues(copy("catalogue.workblocks.result-count", "{visible} of {total} Workblocks"), { visible: rows.length, total: snapshot.content.domainWorkblocks.length })}</span>} />
      <section className="apf-page-section" aria-labelledby="workblock-catalogue-title">
        <div className="apf-section-heading"><div><h2 id="workblock-catalogue-title">{copy("catalogue.workblocks.title", "Workblock catalogue")}</h2><p>{copy("catalogue.workblocks.description", "Open a Workblock to see its five business modules and exact composition.")}</p></div></div>
        <form className="apf-filter-bar" role="search" onSubmit={(event) => { event.preventDefault(); submitSearch(); }}>
          <label className="apf-search-field">
            <Search aria-hidden="true" />
            <span className="apf-visually-hidden">{copy("catalogue.workblocks.search-label", "Search Workblocks, roles and jobs")}</span>
            <input
              ref={catalogueSearchRef}
              type="search"
              placeholder={copy("catalogue.workblocks.search-placeholder", "Search Workblocks, roles or jobs")}
              value={query}
              onFocus={() => { searchBaselineRef.current ??= canonicalSearch; }}
              onBlur={submitSearch}
              onChange={(event) => {
                const value = event.target.value;
                const next = new URLSearchParams(searchParams);
                if (value) next.set("q", value);
                else next.delete("q");
                replaceSearchParams(next);
              }}
            />
          </label>
          {query || workblockFacetKeys.some((facet) => selectedFacetIds[facet].length) ? (
            <button className="apf-button apf-button--quiet" type="button" onClick={clearAll}>
              {copy("component.facet.clear-all", "Clear all")}
            </button>
          ) : null}
        </form>
        <div className="apf-workblock-facets">
          <CatalogueFacet
            id="workblock-role"
            label={copy("catalogue.workblocks.role", "Role")}
            labels={facetLabels}
            options={roleOptions}
            selectedIds={selectedRoleIds}
            onSelectedIdsChange={(ids) => {
              const cleared = ids.length < selectedRoleIds.length;
              updateRepeated("role", ids);
              if (cleared) catalogueSearchRef.current?.focus();
            }}
            open={facetOpen.role}
            onOpenChange={(open) => setFacetOpen("role", open)}
            unavailableSelectedCount={selectedRoleIds.length - availableRoleIds.length}
            onClearUnavailable={() => clearUnavailableFacet("role")}
            onClearAll={clearAll}
          />
          <CatalogueFacet
            id="workblock-job"
            label={copy("catalogue.workblocks.job", "Job")}
            labels={facetLabels}
            options={jobOptions}
            selectedIds={selectedJobIds}
            onSelectedIdsChange={(ids) => {
              const cleared = ids.length < selectedJobIds.length;
              updateRepeated("job", ids);
              if (cleared) catalogueSearchRef.current?.focus();
            }}
            open={facetOpen.job}
            onOpenChange={(open) => setFacetOpen("job", open)}
            unavailableSelectedCount={selectedJobIds.length - availableJobIds.length}
            onClearUnavailable={() => clearUnavailableFacet("job")}
            onClearAll={clearAll}
          />
          {workblockDimensionFacetDefinitions
            .filter(({ param }) => dimensionOptions[param].length)
            .map(({ param }) => (
              <CatalogueFacet
                key={param}
                id={`workblock-${param}`}
                label={dimensionFacetLabel(copy, param)}
                labels={facetLabels}
                options={dimensionOptions[param]}
                selectedIds={selectedDimensionIds[param]}
                onSelectedIdsChange={(ids) => {
                  const cleared = ids.length < selectedDimensionIds[param].length;
                  updateRepeated(param, ids);
                  if (cleared) catalogueSearchRef.current?.focus();
                }}
                open={facetOpen[param]}
                onOpenChange={(open) => setFacetOpen(param, open)}
                unavailableSelectedCount={selectedDimensionIds[param].length - availableDimensionIds[param].length}
                onClearUnavailable={() => clearUnavailableFacet(param)}
                onClearAll={clearAll}
              />
            ))}
        </div>
        {unavailableCount ? <p className="apf-filter-notice" role="status">{replaceCount(copy(unavailableCount === 1 ? "catalogue.workblocks.unavailable.singular" : "catalogue.workblocks.unavailable.plural", unavailableCount === 1 ? "{count} unavailable filter was ignored." : "{count} unavailable filters were ignored."), unavailableCount)} <button type="button" className="apf-link-button" onClick={clearUnavailable}>{copy("component.facet.clear-unavailable", "Clear unavailable filters")}</button></p> : null}
        {rows.length ? (
          <ol className="apf-ruled-index apf-ruled-index--domains">
            {rows.map(({ domainWorkblock: candidate, matchedRoleIds, matchedJobIds, matchedDimensionModules, textMatch }) => {
              const modules = businessModulesFor(snapshot, candidate.id);
              const stage = stageByDomainWorkblock.get(candidate.id);
              const DomainIcon = iconFor(candidate.icon);
              const explanation = relationshipMatchIds(candidate, matchedRoleIds, matchedJobIds);
              const returnState: WorkblocksCatalogueReturnState = { catalogue: "workblocks", returnTo: `/workblocks${canonicalSearch}${location.hash}` };
              const matchReasons: ReactNode[] = [];
              if (textMatch) {
                matchReasons.push(
                  textMatch.kind === "role" && textMatch.roleId
                    ? <Link key="text" to={rolePath(textMatch.roleId)}>{copy("catalogue.workblocks.match.text", "Text")}: {textMatch.label}</Link>
                    : textMatch.jobId
                      ? <Link key="text" to={businessModulePath(candidate.id, textMatch.jobId)} state={returnState}>{copy("catalogue.workblocks.match.text", "Text")}: {textMatch.label}</Link>
                      : <span key="text">{copy("catalogue.workblocks.match.text", "Text")}: {textMatch.label}</span>
                );
              }
              for (const id of explanation.roleIds) {
                matchReasons.push(<Link key={`role:${id}`} to={rolePath(id)}>{copy("catalogue.workblocks.match.role", "Role")}: {roleLabelById.get(id) ?? id}</Link>);
              }
              for (const id of explanation.jobIds) {
                const module = jobById.get(id);
                if (module) {
                  matchReasons.push(<Link key={`job:${id}`} to={businessModulePath(module.domainWorkblockId, module.id)} state={returnState}>{copy("catalogue.workblocks.match.job", "Job")}: {copy(module.labelKey)}</Link>);
                }
              }
              for (const { businessModuleId, matchedDimensionMemberIds } of matchedDimensionModules) {
                const module = jobById.get(businessModuleId);
                const matchedDimensions = workblockDimensionFacetDefinitions.flatMap(({ param }) => {
                  const memberIds = matchedDimensionMemberIds[param] ?? [];
                  if (!memberIds.length) return [];
                  return [
                    `${dimensionMatchLabel(copy, param)}: ${memberIds.map((memberId) => dimensionMemberLabel(copy, param, memberId)).join(", ")}`
                  ];
                });
                if (!matchedDimensions.length) continue;
                matchReasons.push(
                  <span key={`dimensions:${businessModuleId}`}>
                    {module
                      ? (
                        <Link to={businessModulePath(module.domainWorkblockId, module.id)} state={returnState}>
                          {copy("catalogue.workblocks.match.dimensions", "Dimensions")}: {copy(module.labelKey)}
                        </Link>
                      )
                      : (
                        <span>{copy("catalogue.workblocks.match.dimensions", "Dimensions")}: {businessModuleId}</span>
                      )}
                    {`: ${matchedDimensions.join(" · ")}`}
                  </span>
                );
              }
              return (
                <li className="apf-ruled-index__row" key={candidate.id}>
                  <Link className="apf-ruled-index__body apf-ruled-index__link" to={domainWorkblockPath(candidate.id)} state={returnState}>
                    <span className="apf-ruled-index__ordinal">{ordinal(candidate.order)}</span>
                    <div className="apf-ruled-index__heading">
                      <DomainIcon size={20} />
                      <h2 className="apf-ruled-index__name">{copy(candidate.nameKey)}</h2>
                      {candidate.signatureKey ? <span className="apf-system-signature apf-system-signature--row">{copy(candidate.signatureKey)}</span> : null}
                      <span className="apf-ruled-index__kicker">{stage ? copy(stage.labelKey) : copy(candidate.labelKey)}</span>
                    </div>
                    <p className="apf-ruled-index__description">{copy(candidate.descriptionKey)}</p>
                    <div className="apf-ruled-index__meta">
                      <span>{replaceCount(copy("catalogue.workblocks.module-count", "{count} business modules"), modules.length)}</span>
                      <ArrowRight size={16} aria-hidden="true" />
                    </div>
                  </Link>
                  {matchReasons.length ? (
                    <p className="apf-match-reason">
                      <span>{copy("catalogue.workblocks.match.prefix", "Matched by")} </span>
                      {matchReasons.map((reason, index) => <span key={index}>{index ? " · " : null}{reason}</span>)}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : <EmptyState title={copy("catalogue.workblocks.empty.title", "No Workblocks match")} description={copy("catalogue.workblocks.empty.description", "Clear a filter or try a broader role, job or search term.")} action={<button className="apf-button" type="button" onClick={clearAll}>{copy("catalogue.clear-filters", "Clear filters")}</button>} />}
      </section>
    </div>
  );
}
